import { ChangeDetectionStrategy, Component, DestroyRef, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { Router } from '@angular/router';
import { markAllAs } from '@examdojo/angular/forms';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappear } from '@examdojo/animation';
import { SyllabusQuery } from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { PdfViewerComponent } from '@examdojo/core/pdf-viewer';
import { ToastService } from '@examdojo/core/toast';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import {
  RawSourceItem,
  SourceEditorComponent,
  SourceEditorFormService,
  SourceQuery,
  SourceService,
  SourceStatusSelectorComponent,
} from '@examdojo/source';
import { TextInputComponent } from '@examdojo/ui/input';
import { LoaderComponent } from '@examdojo/ui/loader';
import { AngularSplitModule } from 'angular-split';
import isEqual from 'lodash/isEqual';
import { distinctUntilChanged, EMPTY, filter, finalize, forkJoin, merge, of, skip, switchMap, tap } from 'rxjs';
import { CmsUrlParts } from '../../cms/cms.model';
import { DetailViewWrapperComponent } from '../../cms/detail-view/wrapper/detail-view-wrapper.component';
import { ScopedReferenceQuestionListComponent } from '../../reference-question/scoped-list/scoped-reference-question-list.component';
@Component({
  selector: 'exam-source-editor-container',
  standalone: true,
  imports: [
    SourceEditorComponent,
    LoaderComponent,
    DetailViewWrapperComponent,
    SourceStatusSelectorComponent,
    ButtonComponent,
    IconComponent,
    AngularSplitModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    PdfViewerComponent,
    TextInputComponent,
    ScopedReferenceQuestionListComponent,
  ],
  host: { class: 'h-full' },
  templateUrl: './source-editor-container.component.html',
  providers: [SourceEditorFormService],
  animations: [getAnimationAppearDisappear()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SourceEditorContainerComponent {
  constructor(
    private readonly sourceQuery: SourceQuery,
    private readonly sourceService: SourceService,
    private readonly destroyRef: DestroyRef,
    private readonly toastService: ToastService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly sourceEditorFormService: SourceEditorFormService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly router: Router,
  ) {
    merge(this.saveSourceStatusOnControlChange(), this.redirectToListViewOnSyllabusChange())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly isSaving = signal<boolean>(false);

  readonly statusCtrl = this.sourceEditorFormService.statusFormCtrl;
  readonly sourceForm = this.sourceEditorFormService.sourceForm;

  readonly selectedPdfPlace = signal<RawSourceItem['type']>('questions');
  readonly pdfViewer = viewChild<PdfViewerComponent>('pdfViewer');
  readonly sidebarTabs = signal<'details' | 'reference-questions'>('details');

  readonly state = connectState({
    isLoading: this.sourceQuery.selectLoading(),
    source: this.sourceQuery.selectActive(),
    pdfUrls: this.sourceQuery.selectActive().pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id && isEqual(a?.raw_source, b?.raw_source)),
      switchMap((source) =>
        source && source.raw_source
          ? forkJoin({
              markschemes: this.sourceService.getRawSourceUrls('markschemes'),
              questions: this.sourceService.getRawSourceUrls('questions'),
            })
          : of(null),
      ),
    ),
  });

  saveSourceStatusOnControlChange() {
    return this.statusCtrl.valueChanges.pipe(
      switchMap((status) => {
        assertNonNullable(this.state.source?.id, 'sourceId');

        return this.sourceService
          .update(this.state.source?.id, {
            status,
          })
          .pipe(
            tap(() => {
              this.toastService.success('Status updated');
            }),
            this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
          );
      }),
    );
  }

  saveMainForm() {
    if (this.sourceForm.pristine) {
      return;
    }

    markAllAs('touched', this.sourceForm);
    if (!this.sourceForm.valid) {
      this.toastService.error('Please fill in all required fields');
      return;
    }

    const sourceModel = this.sourceEditorFormService.getMainFormValue();

    if (!sourceModel) {
      return;
    }

    assertNonNullable(this.state.source?.id, 'sourceId');

    this.isSaving.set(true);
    this.sourceService
      .update(this.state.source?.id, sourceModel)
      .pipe(
        tap(() => {
          this.sourceForm.markAsPristine();
          this.toastService.success('Source saved successfully');
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY, {
          showErrorDetailsInToast: true,
        }),
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  changeSelectedPdfPlace(value: RawSourceItem['type']) {
    this.selectedPdfPlace.set(value);
  }

  private redirectToListViewOnSyllabusChange() {
    return this.syllabusQuery.selectActive().pipe(
      filter(isNotNullish),
      skip(1), // react only to syllabus changes after the component loads.
      tap((syllabus) => {
        this.router.navigate([CmsUrlParts.Syllabus, syllabus.slug, CmsUrlParts.Sources]);
      }),
    );
  }
}
